/* The set table, as `Builder planu - tydzien - programator` draws it.
 *
 * ── It is a GRID in the canon and a real `<table>` here, and the geometry is identical ───────
 * The canon builds it out of `display: grid` with
 *   `22px 30px repeat(n, minmax(0,1fr)) 40px`
 * and a `display: contents` port of that would have cost the table its semantics: `display:
 * contents` on `<tr>` / `<tbody>` is the one place the property still drops elements out of the
 * accessibility tree in shipping browsers, and this table's whole a11y story is that a screen
 * reader announces "Ciężar, seria 3" from `<th scope>`. `table-layout: fixed` over a `<colgroup>`
 * reproduces the same tracks with the real element, so nothing is traded — and the measurement
 * columns are FLUID here exactly as the canon makes them, so the table fills the card at any
 * column count instead of leaving a ragged right edge.
 *
 * Every other number is the canon's: the 20px header band on `--bg-chip` at 8.5px/700/.1em, the
 * 24px data row, a hairline between every cell, `#` and `Typ` keeping the inset fill down their
 * whole column, and the 30px "+ Seria" foot.
 *
 * ── A selected cell is an OUTLINE, and the cursor says which mode it is in ───────────────────
 * `cursor: cell` unselected, `cursor: text` selected, and a 2px accent outline inset when it is —
 * the mock's own three signals for "this is a spreadsheet cell you may type into". The outline is
 * drawn on the `<td>`, so it is the CELL that is lit and not the field inside it, and it is inset so
 * it cannot overlap the hairline of the cell beside it. The unselected cell carries no edge at all.
 * See the rules themselves for the measurements behind both.
 *
 * ── The three animations are the canon's, from tokens ────────────────────────────────────────
 * `+ Seria` plays `rowIn` chained with `rowFlash`; deleting a set plays `rowOut`. Both are declared
 * here, honour `prefers-reduced-motion` through the single block in `tokens/base.css`, and animate
 * opacity, transform and a colour — never a property that lays the table out again mid-gesture.
 *
 * ── The last header cell is "+ Parametr", and that is where the column menu lives now ────────
 * The canon puts the show/hide-a-column panel behind that header ("Parametry serii", a checkbox
 * list with a sentence about what a removal keeps). It used to be a group inside the exercise's ⋯.
 * It is NOT in both: two paths to one act is how the two drift.
 */

/* The canon's table fills the card: the measurement columns are `minmax(0,1fr)`, so a two-column
 * exercise and a six-column one both reach the same right edge. `overflow-x` stays because a table
 * with six open columns inside a 604px panel still has a floor under which it must scroll rather
 * than clip. */
.tableWrap {
  position: relative;
  width: 100%;
  overflow-x: auto;
  background: var(--bg-inset);
  border-top: var(--border-hairline) solid var(--border);
  border-bottom: var(--border-hairline) solid var(--border);
}

.table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}

/* ── Column tracks ─────────────────────────────────────────────────────────────────────────
 *
 * `22px 30px repeat(n, minmax(0,1fr)) 40px` — the canon's own. The five measurement names are kept
 * as separate classes because a `<col>` needs one, but they all resolve to the same fluid track:
 * what a column IS decides its label and its cell, not its width. */

.colIndex {
  width: 22px;
}

/* The mock's 30px, holding one control: the badge opens the menu. See `SetTypeMenu` for why the
 * cycle and the caret beside it went on 8 September 2026. */
.colType {
  width: 30px;
}

.colReps,
.colWeight,
.colScore,
.colRest,
.colExtra {
  width: auto;
  min-width: 0;
}

.colActions {
  width: 40px;
}

/* Row variants, declared before the cell rules they qualify — stylelint's `no-descending-
 * specificity` keeps a cascade legible by asking the least specific selector to come first. */
.consumedRow td,
.consumedRow th {
  opacity: 0.45;
}

.addRow td {
  height: 30px;
  padding: var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
  border-right: 0;
}

.warningRow td {
  padding: var(--space-1) var(--space-2);
  border-right: 0;
}

/* ── The header band ─────────────────────────────────────────────────────────────────────── */

/* 8.5px is not a reading size and it is not treated as one: the four labels are `#`, `TYP`,
 * `POWT.`, `CIĘŻAR`, learned once and thereafter read as column POSITIONS. The 12 August 2026
 * audit's 10px floor governs prose, and this band is not prose — see `--fs-table-head`. */
.table thead th {
  height: var(--h-set-head);
  padding: var(--space-0);
  font-size: var(--fs-table-head);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-3);
  text-align: center;
  white-space: nowrap;
  background: var(--bg-chip);
  border-right: var(--border-hairline) solid var(--border);
}

.headInner {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  justify-content: center;
  height: 100%;
}

/* The unit the load column is prescribed in — "· kg ▾" / "· % 1RM ▾". Full ink against the header's
 * secondary label, because it is the one part of the header that is a CONTROL. */
.unitButton {
  display: inline-flex;
  gap: var(--space-3px);
  align-items: center;
  padding: var(--space-0);
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-cat);
  color: var(--text);
  text-transform: uppercase;
  background: transparent;
  border: 0;
  border-radius: var(--radius-hair);
}

.unitButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Every optional column carries its own `×`, as the mock draws it — not only one the trainer added.
 * 11px/700 on the tertiary ink, so it reads as part of the header band rather than as a control
 * competing with the label beside it. */
.columnClose {
  padding: var(--space-0) var(--space-1px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  background: transparent;
  border: 0;
  border-radius: var(--radius-hair);
}

.columnClose:hover {
  color: var(--text);
}

.columnClose:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* "+ Parametr" — the gallery lights the whole cell when its panel is open. */
.parameters {
  display: flex;
  gap: var(--space-1);
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.parameters[data-popup-open] {
  color: var(--text);
  background: var(--bg-sunken);
}

.parameters:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* ── Data rows ───────────────────────────────────────────────────────────────────────────── */

.table tbody td,
.table tbody th {
  height: var(--h-set-row);
  padding: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-align: center;
  white-space: nowrap;
  border-top: var(--border-hairline) solid var(--border);
  border-right: var(--border-hairline) solid var(--border);
}

/* The data row stands on the card fill, over the table's own inset ground — which is what makes the
 * `#` and `Typ` columns read as a fixed rail down the left. */
.table tbody tr {
  background: var(--bg-card);
}

/* The `#` cell: the row's own header, and the gallery keeps the inset fill down its whole column. */
.indexCell {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-inset);
}

.typeCell {
  background: var(--bg-inset);
}

.actionsCell {
  border-right: 0;
}

/* ── The type badge (the gallery's `badge()`) ────────────────────────────────────────────── */

.badge {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 20px;
  padding: var(--space-0);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  cursor: pointer;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--radius-xs);
}

.badge:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── The six treatments, `SETC`'s exactly ─────────────────────────────────────────────────
 * `Builder planu - tydzien - programator` states them in its own `SETC` object and the programator
 * redraw takes them: W neutral, T INVERTED (ink ground, card ink), B on the inset ground inside a
 * `--border-2` outline, A in the accent, R the mock's own #5b7cff, D lime.
 *
 * **This departs from mobile, and the departure is the owner's to accept or revert.** The 3 August
 * 2026 ask was that a set type be the same colour here as on the phone, so a trainer authoring a
 * back-off and the athlete meeting it in the logger see one concept; four of these six now differ.
 * The mobile family is untouched in `colors.css` as `--set-*` and every other surface still reads
 * it, so reverting is these six declarations and nothing else.
 *
 * The transparent border keeps every badge the mock's 24 x 20 box whichever treatment it wears. */
.badgeWarmup {
  color: var(--setc-warmup-ink);
  background: var(--setc-warmup-fill);
}

.badgeWorking {
  color: var(--setc-working-ink);
  background: var(--setc-working-fill);
}

.badgeTop {
  color: var(--setc-top-ink);
  background: var(--setc-top-fill);
}

.badgeBackoff {
  color: var(--setc-backoff-ink);
  background: var(--setc-backoff-fill);
  border-color: var(--setc-backoff-edge);
}

.badgeAmrap {
  color: var(--setc-amrap-ink);
  background: var(--setc-amrap-fill);
}

/* Rendered, never authored — see `badgeOf`. */
.badgeDropset {
  color: var(--setc-dropset-ink);
  background: var(--setc-dropset-fill);
}

.badgeRaw {
  color: var(--text-2);
  border-color: var(--border-2);
}

/* ── Cells ───────────────────────────────────────────────────────────────────────────────── */

.cell {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  align-items: center;
  justify-content: center;
  height: 100%;
}

.cellRow {
  display: flex;
  gap: var(--space-5px);
  align-items: baseline;
  justify-content: center;
}

/* The canon's own two cursors, and they are the affordance: `cell` says "this is a spreadsheet cell
 * you may select", `text` says "you are in it and may type". Nothing else on the card uses either,
 * so the mode is unambiguous without a second mark. */
.input {
  width: 100%;
  padding: var(--space-0);
  font-family: var(--font-num);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: center;
  cursor: cell;
  background: transparent;
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--radius-hair);
  font-variant-numeric: tabular-nums;
}

/* The day being READ — the client calendar's preview of a session that has not happened.
 *
 * `readonly` rather than `disabled`, so the value stays selectable and the cell stays a tab stop:
 * this surface exists to be read, and a disabled field drops out of the tab order taking the arrow
 * navigation with it. The ink drops one step because nothing here can be typed into.
 *
 * `--text-2` and not `--text-3`: this is the prescription itself, at 12px, and it is the only thing
 * on the surface. `--text-3` measured 2.86:1 on `--bg-inset` in light — the note this repo already
 * records against the set-type menu's description line. */
.input[readonly] {
  color: var(--text-2);
  cursor: default;
}

/* The unit where its toggle stood — see the note at the `readOnly` branch in `SetTable`. The word
 * is load-bearing: 82,5 means one thing in kilograms and another as a percentage of a maximum. */
.unitStatic {
  color: var(--text-2);
  font-size: var(--fs-tag);
  font-weight: var(--fw-extrabold);
}

/* The selected cell — the mock's `2px solid var(--accent)` at `outline-offset: -2px`, and its
 * `text` cursor.
 *
 * ── It is the CELL that is outlined, not the field inside it (8 September 2026) ──────────────
 * The mock states the selected treatment in `cellSt`'s own selected branch, on the 24px grid cell:
 * the ember sits INSIDE the cell's box, against the row's `--bg-card`, and touches the hairlines of
 * the cells either side. This app drew it on `.input`, which is a 15px-tall band and — on a cell
 * carrying a unit — only `5ch` wide, so the mark was a small floating box in the middle of a cell
 * rather than the cell being lit. Measured at 1600 x 1000 before the change: 127 x 15 against the
 * mock's 150 x 24; after it, the two are the same box.
 *
 * `:has()` on the `<td>` rather than a class threaded down through `SetCell`: the address attribute
 * the arrow navigation already publishes is what identifies a value cell, so nothing new reaches the
 * component's props and the type badge and the two row buttons are untouched. `:focus` rather than
 * `:focus-visible`, as before — a cell selected with the pointer is as selected as one arrowed onto,
 * and the current cell of a spreadsheet must always be visible.
 *
 * The unselected cell beside it is the mock's too, which is to say it carries NOTHING: `cursor:
 * cell` and no edge. A `--border-2` hairline appeared under the pointer here until the same day; the
 * mock draws no hover state on a value cell, and a second box that comes and goes on a table of
 * sixty cells reads as a selection the trainer did not make. */
.table td[data-set-cell]:has(.input:focus) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.input:focus {
  cursor: text;
  outline: none;
}

.secondary .input {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* ── A cell with a unit prints ONE value ─────────────────────────────────────────────────────
 *
 * The mock's weight cell reads "60 kg", centred, as a single thing. Ours read "60" centred and "kg"
 * pinned to the cell's right edge, because `.input` is `width: 100%` and pushed the suffix out — so
 * a trainer scanning the column saw a number column and a unit column where the mock draws one
 * value. The field therefore sizes to its content when a unit stands beside it, and the two centre
 * together as a pair.
 *
 * `5ch` in the numeric face fits every load this app can hold at the wire's own bound — four digits
 * plus a decimal comma — and a longer draft scrolls inside the field rather than reflowing the row.
 * The hit target narrows by about a third of the cell, which is the honest cost and is why the
 * unsuffixed cells (the great majority — repetitions, RPE, tempo) are untouched. */
.cellRow[data-unit] .input {
  flex: none;
  width: 5ch;
  text-align: right;
}

.suffix {
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* A cycle whose value does not exist yet — a rule that reads the client's own history at workout
 * start. It takes the derived estimate's "not knowable yet" violet on purpose: the two say the same
 * thing about the same kind of number, and a second hue would invent a distinction. Italic and
 * wrapping, because it is a sentence in a cell sized for four digits. */
.resolved {
  display: block;
  font-size: var(--fs-label-sm);
  font-style: italic;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-snug);
  text-align: right;
  color: var(--pm-violet-ink);
}

/* The gallery's `s.sub`: an italic estimate under a derived load — "~85 kg". Rendered in the
 * "not knowable yet" violet, mixed toward `--text` so it reads in both themes. */
.derived {
  display: block;
  font-size: var(--fs-label-sm);
  font-style: italic;
  font-weight: var(--fw-semibold);
  line-height: var(--lh-none);
  color: var(--pm-violet-ink);
}

/* ── The foot: "+ Seria" ─────────────────────────────────────────────────────────────────── */

/* The canon's foot: one dashed chip, centred across the whole table rather than a glyph pinned into
 * the `#` column. */
.addInner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
}

.addSet {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  height: 20px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-xs);
}

.addSet:hover {
  color: var(--text);
  border-color: var(--text-2);
}

.addSet:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.addSet[aria-disabled='true'] {
  cursor: default;
}


/* ── Row controls ────────────────────────────────────────────────────────────────────────── */

.rowControls {
  display: flex;
  gap: var(--space-2px);
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-2);
}

/* `position: relative` is load-bearing, not cosmetic: the hit-area pseudo-element below is
 * `position: absolute`, so on a static button it resolves against `.tableWrap` instead and paints a
 * transparent sheet over the whole table that swallows every click in it. Found by driving the
 * suite, not by reading the file. */
.rowButton {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: var(--space-13px);
  height: var(--space-13px);
  padding: var(--space-0);
  color: inherit;
  background: transparent;
  border: 0;
  border-radius: var(--radius-hair);
}

.rowButton svg {
  display: block;
  width: var(--space-13px);
  height: var(--space-13px);
  fill: none;
}

.rowButton:hover {
  color: var(--text);
}

.rowButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.rowButton[aria-expanded='true'] {
  color: var(--text);
}

/* The pointer target the 13px glyph does not carry on its own — WCAG 2.2 SC 2.5.8's floor, bought
 * with a pseudo-element so nothing visible moves. */
.rowButton::after {
  position: absolute;
  inset: var(--hit-extend);
  content: "";
}

.warning {
  display: block;
  padding: var(--space-3px) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--danger-text-strong);
  text-align: left;
  white-space: normal;
  background: var(--danger-soft);
  border-radius: var(--radius-xs);
}

.noColumns {
  /* A row rather than a paragraph since the ⓘ joined it: the statement and the glyph share one
   * line, which is what keeps a card with nothing to prescribe one line tall. */
  display: flex;
  gap: var(--space-1);
  align-items: center;
  padding: var(--space-6px) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-xs);
}

.table thead th:last-child {
  border-right: 0;
}

/* The mock's popup is 245px and this one ran to 315: the five descriptions are longer here than
 * the canon's and they are the owner's copy, not this round's to shorten. So the BOX is capped
 * instead and the two long lines wrap. `--menu-max-width` rather than a second `max-width` — see
 * `MenuSurface.module.css`, which owns the declaration. */
.typePopup {
  /* The mock's own popup: 252px at radius 13, padded 7. Set through `MenuSurface`'s four hooks
   * rather than by redeclaring its properties from here — see that file for the chunk-order reason. */
  --menu-min-width: 252px;
  --menu-max-width: 252px;
  --menu-pad: var(--space-7px);
  --menu-radius: var(--r-field);
}

/* The parameter panel's own, one step wider and a step less round: 258px at radius 12, padded 8. */
.parameterPopup {
  --menu-min-width: 258px;
  --menu-max-width: 258px;
  --menu-pad: var(--space-2);
  --menu-radius: var(--radius-12px);
}

/* ── The type menu's rows ─────────────────────────────────────────────────────────────────────
 *
 * `Builder planu - tydzien - programator`'s `typeOpts`, value for value: a row at `7px 8px` on a
 * 9px radius with a 9px gap, a 26 x 24 badge at radius 8 in 11/700, the name at 12/600 and the
 * description at 10/500 on the tertiary ink. The row the set already IS takes `--bg-inset` and
 * nothing else.
 *
 * This REPLACES mobile's own sheet shape, which stood here from 3 August 2026 — a 32px badge, 12px
 * padding, a hairline round every row and the accent taking over the current one's fill and border.
 * The programator states a denser list and, on this surface, the mock decides what is visible. What
 * survives the swap is the badge borrowing the six role classes above, so the menu and the table
 * cannot drift apart on what a back-off looks like.
 *
 * The tick went with the accent ring: the mock draws neither, and the current row is already named
 * as current in `choice.ariaLabel`, which is the channel that was never colour.
 *
 * These do NOT reuse `MenuSurface.module.css`'s `.item`. That class is a single-line label with a
 * trailing hint, and this is a two-line row with a badge in front of it. */

.typeItem {
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  padding: var(--space-7px) var(--space-2);
  cursor: default;
  border-radius: var(--radius-mark);
}

/* The row the set already is. */
.typeItemOn {
  background: var(--bg-inset);
}

/* A menu item is not a tab stop, so the roving highlight is the ONLY focus signal a keyboard user
 * gets. The same inset `--text` ring the shared surface uses. */
.typeItem[data-highlighted] {
  box-shadow: var(--ring-menu);
}

/* The choice the menu shows but will not take — back-off on an exercise with no top. Still
 * highlightable, so the reason in its description reaches the keyboard too. */
.typeItemRefused {
  opacity: 0.55;
}

.typeBadge {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 24px;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--radius-8px);
}

.typeMid {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.typeName {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

/* `--text-3`, the mock's own ink for this line, and a departure from the note this file carried
 * from 3 August 2026 to 8 September 2026: `--text-3` on `--bg-card` measures 3.01:1 light and
 * 4.02:1 dark, and the previous round moved it to `--text-2` for that reason. On the programator
 * redraw the mock decides what is visible and a contrast objection to a value it states is the
 * owner's to make, so it goes back and is flagged rather than silently corrected. */
.typeDesc {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

/* ── The row entrance and the row exit, both transcribed ─────────────────────────────────────
 *
 * `+ Seria` plays the canon's `rowIn` chained with `rowFlash .7s ease .2s`: the row drops in, then
 * a green wash holds and fades. They are two animations on two elements rather than a comma list on
 * one, because the value allowlist takes a single token per animation property — correctly, since a
 * comma list is where a literal duration would smuggle itself in. The movement is on the `<tr>` and
 * the wash on its cells, which is the only split that leaves both names, both durations and both
 * curves the canon's.
 *
 * Deleting a set plays `rowOut`: a red wash, a fade, and the row's own height collapsing to zero.
 * The canon animates `max-height` on a grid row; a `<tr>` does not answer to it, so the CELLS
 * animate their height instead and the row collapses with them. Same three stops, same 240ms, same
 * `cubic-bezier(.4,0,.7,.2)`.
 *
 * **The removal is not deferred to play it.** `removeSet` reaches the store on the press, and the
 * row on screen is drawn from the view it last had — see `model/departures` for why that
 * distinction is the whole design, and for what an undo inside the window does. A departing row is
 * `inert` and `aria-hidden`, so nothing in it is reachable while it leaves.
 *
 * Opacity, transform, height and a colour only. The single `prefers-reduced-motion` block in
 * `tokens/base.css` neutralises all of them; no rule here restates it, because one place is the
 * policy.
 */

@keyframes row-in {
  0% {
    opacity: 0;
    transform: translateY(-6px);
  }

  55% {
    opacity: 1;
    transform: none;
  }

  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes row-flash {
  0% {
    background: var(--good-soft);
  }

  100% {
    background: var(--bg-card);
  }
}

@keyframes row-out {
  0% {
    height: var(--h-set-row);
    opacity: 1;
    background: var(--row-out-wash);
  }

  60% {
    opacity: 0.4;
    background: var(--row-out-wash-fade);
  }

  100% {
    height: 0;
    opacity: 0;
    background: transparent;
  }
}

.rowEntering {
  animation-name: row-in;
  animation-duration: var(--dur-row-in);
  animation-timing-function: var(--ease-fp);
  animation-fill-mode: both;
}

.table tbody tr.rowEntering > td,
.table tbody tr.rowEntering > th {
  animation-name: row-flash;
  animation-duration: var(--dur-row-flash);
  animation-delay: var(--dur-flash-delay);
  animation-timing-function: var(--ease-default);
  animation-fill-mode: both;
}

.table tbody tr.rowLeaving > td,
.table tbody tr.rowLeaving > th {
  overflow: hidden;
  animation-name: row-out;
  animation-duration: var(--dur-row-out);
  animation-timing-function: var(--ease-exit);
  animation-fill-mode: both;
}

/* ── A change under review (board U) ─────────────────────────────────────────────────────────
 *
 * The same table, read-only and drawn as text: a value that moved shows the old one struck in the
 * secondary ink before the new one, a set the change adds says so in its `#` cell („+ 5", in full
 * ink), and a set it takes away is struck whole. No row controls, so no trailing column; the `#`
 * and `Typ` tracks take the board's 36 and 44px, room for „+ 5" and a struck letter beside a badge.
 * The secondary ink rather than the tertiary: `--text-3` does not hold its contrast on these rows. */
.table[data-changes] .colIndex {
  width: 36px;
}

.table[data-changes] .colType {
  width: 44px;
}

/* The old value and the new one wrap onto two lines rather than spilling into the next cell: five
 * value columns in a 448px panel leave a column about 50px wide. */
.table[data-changes] tbody tr > td {
  height: auto;
  min-height: var(--h-set-row);
  padding: var(--space-2px) var(--space-0);
  white-space: normal;
}

.changeValue {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-0) var(--space-5px);
  align-items: baseline;
  justify-content: center;
  max-width: 100%;
  font-family: var(--font-num);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
}

.nowValue {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: baseline;
  white-space: nowrap;
}

.nowValue[data-new] {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.previousValue {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-decoration: line-through;
}

.table tbody tr[data-change="added"] th {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.table tbody tr[data-change="removed"] th,
.table tbody tr[data-change="removed"] td {
  color: var(--text-2);
  text-decoration: line-through;
}
